/**
 * Database Blueprint types.
 *
 * Per-blueprint settings file on disk: jamsql-blueprint.json
 *
 * Shared between renderer (React) and main (Electron) processes.
 * Imported from Electron via relative path:
 *   import type { ... } from '../../src/features/database-blueprint/types/databaseBlueprintTypes';
 */

export type UserFacingEngineId = 'mssql' | 'pgsql' | 'mysql' | 'oracle' | 'sqlite';

/** Mirrors the main-process `RegistryEntry` in electron/database-blueprint/blueprintRegistry.ts. */
export type BlueprintRegistryEntry = { id: string; folderPath: string; label: string };

/**
 * One linked (connection, database) pair within a Database Blueprint folder.
 * A blueprint folder can be linked to 1..N peers; each carries its own label
 * and its own last-sync timestamp.
 */
export type LinkedDb = {
  /** User-supplied label (e.g. "Dev", "Stage", "Prod"). Used in MetaInfo
   *  filename (`jamsql-meta-<label>.json`) and in the Connections panel. */
  label: string;
  connectionId: string;
  /** Stored for display when the connection is gone or renamed. */
  connectionDisplayName: string;
  database: string;
  engine: UserFacingEngineId;
  /** ISO timestamp of last successful Refresh / Apply against this link. */
  lastSyncAt: string | null;
};

/**
 * Object-type include flags for the blueprint folder. Engine-irrelevant fields
 * are ignored (e.g. `packages` only matters for Oracle).
 */
export type BlueprintObjectTypes = {
  tables: boolean;
  views: boolean;
  procedures: boolean;
  functions: boolean;
  triggers: boolean;
  userTypes: boolean;
  sequences: boolean;
  synonyms: boolean;
  permissions: boolean;
  schemas: boolean;
  /** Oracle-only; ignored on other engines. */
  packages: boolean;
};

/**
 * Default include flags — everything ON. Engine-aware checkbox lists
 * disable irrelevant rows in the setup UI.
 */
export const DEFAULT_MIRROR_OBJECT_TYPES: BlueprintObjectTypes = {
  tables: true,
  views: true,
  procedures: true,
  functions: true,
  triggers: true,
  userTypes: true,
  sequences: true,
  synonyms: true,
  permissions: true,
  schemas: true,
  packages: true,
};

/**
 * The object types the emitter writes one `.sql` file per object for.
 *
 * `userTypes` became a real folder in 1.4.21 (S8): SQL Server user-defined
 * types are emitted as `Programmability/User-Defined Types/<schema>.<Name>.sql`
 * and parsed back by `electron/database-blueprint/ddl-parse/parseUserType.ts`.
 * It is the one key whose answer depends on more than the manifest's own
 * checkbox — see `blueprintEmitsFiles`'s `userTypesAvailable` parameter —
 * because a folder written BEFORE 1.4.21 carries a ticked checkbox and no
 * files, and comparing a live SQL Server against it would read every type as
 * target-only and script a `DROP TYPE`.
 */
export type BlueprintEmittedTypeKey =
  | 'tables'
  | 'views'
  | 'procedures'
  | 'functions'
  | 'sequences'
  | 'userTypes';

/**
 * Engines that have a SEQUENCE concept Schema Compare can apply.
 *
 * Lives here rather than in `electron/database-blueprint/ddl-parse/parseSequence.ts`
 * (which re-exports it, so every existing importer is unchanged) because it is
 * half of the `blueprintEmitsFiles` contract and BOTH sides of that contract now
 * need it: the emitter, to decide whether to write `Sequences/*.sql` at all, and
 * the two compare-cap consumers, to decide whether the folder can be expected to
 * contain any. It is a per-engine fact with no main-process dependencies, so it
 * belongs in the module both processes already share.
 */
export const SEQUENCE_ENGINES = ['pgsql', 'mssql', 'oracle'] as const;

/**
 * `undefined` / unrecognised → `false`, deliberately. The only caller that can
 * hit that is a compare cap derived from a hand-edited manifest with no usable
 * link engine, and capping there costs a sequence comparison the folder almost
 * certainly could not have populated. NOT capping does not currently emit a
 * `DROP SEQUENCE` either way — see `blueprintEmitsFiles`'s doc comment for what
 * an uncapped sequence mismatch actually does today, and its merge-gate note
 * for when that stops being true. The cap is recorded in `scopeCapKeys`, so it
 * can never leak into the user's saved per-engine defaults.
 */
export function engineHasSequences(engine: string | undefined): boolean {
  return (SEQUENCE_ENGINES as readonly string[]).includes(engine ?? '');
}

/**
 * S7/S8 — engines whose Schema Compare enumerates user-defined types (SQL
 * Server: table / alias / CLR types), and therefore the engines whose blueprint
 * folders carry `Programmability/User-Defined Types/*.sql`.
 *
 * BOTH sides of the contract ask this: the emitter, to decide whether to write
 * the folder at all, and the two compare-cap producers, which cap
 * `includeUserTypes` for a blueprint of one of these engines only — a cap on
 * any other engine would narrow a declared scope for an object kind that engine
 * cannot have.
 */
export const USER_TYPE_ENGINES = ['mssql'] as const;

export function engineHasUserTypes(engine: string | undefined): boolean {
  return (USER_TYPE_ENGINES as readonly string[]).includes(engine ?? '');
}

/**
 * S8 — does this folder's manifest RECORD an emit that included user types?
 *
 * `includedObjectTypes.userTypes` cannot answer that on its own: it defaults to
 * `true` and the checkbox has shipped since the first release, so every folder
 * written before 1.4.21 claims types it has no files for. `emittedTypes` is
 * written by the emit paths only (setup + Refresh-from-DB), so its ABSENCE is
 * exactly "emitted by a version that could not write them", and a manifest edit
 * that ticks the box without a following refresh does not fake it either.
 *
 * Consulted for `userTypes` ONLY. The other four keys have had files since the
 * first release, so an absent marker says nothing about them and capping them
 * on it would silently narrow every pre-1.4.21 folder's compare.
 */
export function manifestRecordsUserTypeFiles(
  meta: { emittedTypes?: ReadonlyArray<BlueprintEmittedTypeKey> } | undefined,
): boolean {
  return (meta?.emittedTypes ?? []).includes('userTypes');
}

/**
 * Does the emitter write files for each object type, given this manifest?
 *
 * THE PRODUCER, not a model of it: `ddl-emit/sharedEmitter.ts` calls this to
 * decide what it writes, so everything else that calls it is asking the emitter
 * rather than restating its rules. Schema Compare's folder side is built purely
 * from the files on disk (`electron/database-blueprint/folderSchemaFetcher.ts`),
 * so a type this function reports as emitted is EXPECTED to have files there —
 * not guaranteed to.
 *
 * Fix round 5 correction: this answers from the MANIFEST
 * (`includedObjectTypes`), not a directory listing, and the two can disagree —
 * a Settings edit that widens a type but is saved without a following
 * "Refresh from DB", a hand-edited manifest, or a stale/partial emit. When
 * they disagree, this function still says the type is emitted, the compare
 * does not cap it, `walkSql` on the actually-empty folder silently returns
 * `[]`, and every instance of that type on the live target reads target-only
 * → `removed` → scriptable → a real `DROP` in the generated script for
 * tables/views/procedures/functions. Capping on folder emptiness instead of
 * the manifest was considered and rejected — this wave's standing ruling is
 * that a false negative (silently narrowing what gets compared) is worse than
 * the false positive this causes — so the fix here is disclosure, not a
 * capping change. Disclosed in the public docs (`database-blueprint.njk` →
 * "Editing a blueprint's settings") and owed to the knowledge pack (see the
 * W3.4 report's FOR FIN).
 *
 * `sequences` is the one type in that same family where a manifest/folder
 * mismatch is currently harmless rather than destructive: a target-only
 * sequence still classifies `removed` and is still selectable, but
 * `schemaScriptGenerator` has no sequence-emitting code path at all today, so
 * `generateSyncScript` returns an empty script with no warning for it — a
 * phantom entry in the results tree, not a `DROP SEQUENCE`. That gap belongs
 * to W1.4 ("selected sequences: emit or say so"). MERGE-GATE NOTE: the moment
 * W1.4 makes the generator emit sequences, this stops being harmless and
 * becomes the same real `DROP` the other four types already produce —
 * revisit this paragraph, `engineHasSequences`'s doc above, and the two
 * compare-cap producers — `schema-compare/utils/folderCompareScope.ts` (the
 * renderer's single producer, shared by the single-link Apply and the
 * Apply-to-linked-DBs batch since #89) and `electron/mcp-blueprint/blueprint.ts`
 * — when that lands; none of them should still call it a phantom afterward.
 *
 * The gates were NOT uniform before this function existed — four types were
 * truthiness and `sequences` alone was `!== false` — which is exactly the trap:
 * a reader who samples one gate and generalises gets four of the five backwards
 * (twice, in this task's own history). The asymmetry is real, and it now lives
 * in ONE place instead of being restated in prose next to three call sites.
 *
 * `types === undefined` is a legacy manifest-less blueprint: everything is
 * emitted, so nothing is capped.
 *
 * `engineHasSequences` — EVERY caller passes the real predicate, keyed on the
 * engine the blueprint folder itself was emitted for. Fix round 3 let the two
 * compare-cap consumers hardcode `true`, reasoning that an engine without
 * sequences has none at either endpoint. That reasoning only holds for a
 * same-engine compare, and nothing enforces one: `SchemaCompareSetupDialog`'s
 * target picker lists every connected connection regardless of the source's
 * engine, and cross-engine compare is a first-class feature. So a MySQL or
 * SQLite blueprint — whose folder genuinely has zero `Sequences/*.sql` — was
 * reported as EMITTING sequences, `includeSequences` went uncapped, the target
 * was re-pointed to PostgreSQL/Oracle/MSSQL through the ordinary "Change" flow,
 * and every real sequence there compared against an empty folder set, read
 * target-only → `removed` — today a phantom entry in the results tree (see
 * above), not the `DROP SEQUENCE` this paragraph claimed before fix round 5.
 */
export function blueprintEmitsFiles(
  types: Partial<BlueprintObjectTypes> | undefined,
  engineHasSequences: boolean,
  /**
   * S8 — "can user-type files exist for this endpoint at all", which the caller
   * answers because the two sides of the contract ask different questions:
   *
   *  - the EMITTER passes `engineHasUserTypes(engine)` — it is about to write
   *    the folder, so the engine is the only open question;
   *  - a COMPARE CAP passes `engineHasUserTypes(blueprintEngine) &&
   *    manifestRecordsUserTypeFiles(meta)` — a folder emitted before 1.4.21 has
   *    the checkbox ticked and no files, and reading the live side for types it
   *    cannot see there scripts a `DROP TYPE` for every one of them.
   *
   * Defaults to `false`: an unaware caller gets the non-destructive answer.
   */
  userTypesAvailable = false,
): Record<BlueprintEmittedTypeKey, boolean> {
  if (!types) {
    return {
      tables: true,
      views: true,
      procedures: true,
      functions: true,
      sequences: true,
      // A manifest-less blueprint has no checkbox to read, so the caller's
      // availability answer is the whole answer.
      userTypes: userTypesAvailable,
    };
  }
  return {
    tables: Boolean(types.tables),
    views: Boolean(types.views),
    procedures: Boolean(types.procedures),
    functions: Boolean(types.functions),
    // The one exception, and the reason this function has to exist: a manifest
    // that says nothing about sequences still gets them.
    sequences: types.sequences !== false && engineHasSequences,
    // Truthiness, NOT `!== false`: a manifest with a hole where `userTypes`
    // should be predates the field or was hand-edited, and the non-destructive
    // reading of a hole is "no files" (see `userTypesAvailable` above).
    userTypes: Boolean(types.userTypes) && userTypesAvailable,
  };
}

/**
 * S8 — the `emittedTypes` marker to stamp on a manifest right after an emit.
 *
 * Derived from `blueprintEmitsFiles` rather than hand-listed, so what is
 * recorded is exactly what the write gates produced. `engine` is the LINK's
 * engine — the one the folder was emitted from.
 */
export function emittedTypesForManifest(
  types: Partial<BlueprintObjectTypes> | undefined,
  engine: string | undefined,
): BlueprintEmittedTypeKey[] {
  const emits = blueprintEmitsFiles(
    types,
    engineHasSequences(engine),
    engineHasUserTypes(engine),
  );
  return (Object.keys(emits) as BlueprintEmittedTypeKey[]).filter((key) => emits[key]);
}

/**
 * Object kinds that can be excluded from a blueprint folder. Matches the
 * categories the emitter walks; `linkLabel` (optional, unused in v1) reserves
 * room for per-link exclusions if we need them later.
 */
export type BlueprintExcludedObjectKind =
  | 'table'
  | 'view'
  | 'procedure'
  | 'function'
  | 'trigger'
  | 'userType'
  | 'sequence'
  | 'synonym'
  | 'permission'
  | 'package';

export type BlueprintExcludedObject = {
  kind: BlueprintExcludedObjectKind;
  schema: string;
  name: string;
  /** Reserved for v2 per-link scoping; unset means folder-wide. */
  linkLabel?: string;
};

/**
 * Tables the user opted to blueprint with their *data* in addition to their
 * schema (DDL). For each entry the emitter writes a `[schema].[name].data.sql`
 * file next to the DDL file containing an upsert script for the table's rows.
 *
 * Designed for small lookup tables (currency codes, status enums, etc.).
 * Tables with hundreds of rows get an orange warning in the picker; tables
 * with thousands a red one — Database Blueprint is not meant to replace a real
 * data dump for large tables.
 *
 * `allowDestructiveReplay` only matters for tables without a primary key.
 * Default (`undefined` / `false`) skips them with a "no primary key"
 * reason. Setting `true` opts the user into a DELETE + INSERT replay
 * script (idempotent but destructive — wipes existing rows on each
 * replay).
 */
export type BlueprintDataTable = {
  schema: string;
  name: string;
  allowDestructiveReplay?: boolean;
};

/**
 * Lightweight `(schema, table)` reference used by the Slice 10 Sync Data
 * dialog when listing the blueprint-dataed tables currently present in a
 * blueprint folder. Now also carries `allowDestructiveReplay` (sourced from
 * `jamsql-blueprint.json`'s `blueprintDataTables` array) so the Sync Data progress
 * orchestrator can route no-PK tables to the destructive replay path.
 */
export type BlueprintDataTableRef = {
  schema: string;
  table: string;
  /** Propagated from `BlueprintDataTable.allowDestructiveReplay` in jamsql-blueprint.json. */
  allowDestructiveReplay?: boolean;
};

/**
 * Per-blueprint settings persisted at `jamsql-blueprint.json` in the blueprint root.
 * Versioned so future schema changes can migrate forward without surprising users.
 */
export type BlueprintMetadata = {
  version: 1;
  /** 1..N peers. */
  linkedDbs: LinkedDb[];
  includedObjectTypes: BlueprintObjectTypes;
  /** User schemas only (system schemas off by default; user can opt in). */
  includedSchemas: string[];
  /**
   * Folder-wide exclusions — objects the user explicitly opted out of the blueprint
   * (e.g. temporary tables, archive junk). Excluded objects are:
   *   - never emitted (no `.sql` file written / kept up to date for them),
   *   - filtered out of preview / three-way merge / folder snapshot,
   *   - skipped from the Apply-to-DB source endpoint.
   * Absent or empty `excludedObjects` means no exclusions. Older
   * `jamsql-blueprint.json` files (pre-exclusions) load with an empty list.
   */
  excludedObjects?: BlueprintExcludedObject[];
  /**
   * Tables whose data should also be exported as a `.data.sql` upsert script
   * alongside their DDL. Absent / empty means "DDL only" (the previous
   * behaviour, fully backwards-compatible).
   */
  blueprintDataTables?: BlueprintDataTable[];
  /**
   * Which linked DB the user has selected as "active" for this blueprint folder
   * — drives the Database Blueprint toolbar's link chip and any operation that
   * needs a default link (Refresh-from-DB, Apply, Sync data, etc.). Must
   * match one of the labels in `linkedDbs`. When unset (legacy blueprints or
   * blueprints that haven't switched yet), readers fall back to the first
   * entry in `linkedDbs`. See `resolveActiveLinkLabel` in
   * `src/features/database-blueprint/utils/activeLink.ts` for the canonical
   * resolver.
   */
  activeLinkLabel?: string;
  /**
   * Whether the blueprint folder's filesystem treats filenames
   * case-insensitively (HFS+ default, NTFS, exFAT). Probed once at blueprint
   * creation via `detectFsCaseInsensitive` and persisted here so every
   * subsequent operation (write/read sidecar, prune) uses the same value
   * without re-probing the disk on every call.
   *
   * `undefined` on pre-existing blueprints (written before this field was
   * added). Consumers probe on-demand and update the manifest at next
   * refresh. Default assumption when absent: case-insensitive is `false`
   * (i.e. fall back to legacy unsuffixed names — safe on case-sensitive FS;
   * on case-insensitive FS two colliding tables would still silently
   * overwrite each other, same as the pre-fix behaviour).
   */
  fsCaseInsensitive?: boolean;
  /**
   * S8 — which object types the folder's `.sql` files were actually EMITTED
   * for, stamped by the emit paths (setup and Refresh-from-DB) via
   * `emittedTypesForManifest`. `includedObjectTypes` records what the user
   * ASKED for; this records what a run of the emitter that honoured it wrote.
   *
   * `undefined` on every folder written before 1.4.21 — which is the point:
   * `manifestRecordsUserTypeFiles` reads that absence as "this folder has no
   * `Programmability/User-Defined Types/` files", so a folder→DB compare leaves
   * user-defined types out of scope (and says so) instead of scripting a
   * `DROP TYPE` for each one. The next Refresh-from-DB stamps it.
   */
  emittedTypes?: BlueprintEmittedTypeKey[];
  /** Bumped when the engine's folder layout changes. */
  layoutVersion: 1;
};

/**
 * IPC request: set up a new blueprint folder (one-shot, one connection in v1).
 */
export type SetupBlueprintRequest = {
  /** Target folder path (existing empty folder OR new folder that will be created). */
  folderPath: string;
  connectionId: string;
  database: string;
  engine: UserFacingEngineId;
  /** Display name for jamsql-blueprint.json. */
  connectionDisplayName: string;
  /** Label for the first linked DB (defaults to engine + database if blank). */
  label: string;
  includedObjectTypes: BlueprintObjectTypes;
  includedSchemas: string[];
  /** Whether to write a default .gitignore on first setup. */
  writeGitignore: boolean;
  /**
   * Objects to exclude from the blueprint (won't be emitted, won't surface in
   * preview/three-way diffs as added/removed). Optional — empty/omitted means
   * "no exclusions". Configured via the "Set exclusions…" sub-dialog on the
   * setup screen.
   */
  excludedObjects?: BlueprintExcludedObject[];
  /**
   * Tables the user opted to blueprint with data (DDL + `.data.sql` upsert).
   * Optional — empty/omitted means DDL only.
   */
  blueprintDataTables?: BlueprintDataTable[];
};

/**
 * IPC request: write data-dump sidecar (`*.data.sql`) for selected tables.
 * Called by the renderer after a successful Refresh / Setup, so the heavy
 * row-by-row SELECT happens out of the main emit path and can be retried
 * independently when it fails.
 */
export type WriteBlueprintDataRequest = {
  folderPath: string;
  connectionId: string;
  database: string;
  engine: UserFacingEngineId;
  tables: BlueprintDataTable[];
  /** Whether the blueprint folder's FS is case-insensitive. Optional — defaults to
   *  false (legacy behaviour). When true the emitter uses the disambiguated
   *  sidecar filename strategy from `sidecarFileName`. */
  fsCaseInsensitive?: boolean;
};

export type WriteBlueprintDataResult =
  | { success: true; written: Array<{ schema: string; name: string; rows: number }>; skipped: Array<{ schema: string; name: string; reason: string }> }
  | { success: false; error: string };

/**
 * Pro-required error arm for setup/build operations that exceed the 200-object
 * Personal-mode limit. Structurally compatible with the global `ProRequiredError`
 * in `electron.d.ts` — the `error: 'requires_pro'` discriminant lets callers
 * detect it with `isProRequiredError()` and dispatch to the UpgradeDialog.
 */
export type SetupBlueprintProRequiredResult = {
  success: false;
  error: 'requires_pro';
  feature: string;
  featureName: string;
  featureDescription: string;
};

export type SetupBlueprintResult =
  | {
      success: true;
      folderPath: string;
      metadata: BlueprintMetadata;
      objectCounts: BlueprintObjectCounts;
      /**
       * Optional, additive — set when the schema emit succeeded but the
       * blueprint-data capture failed or skipped tables (e.g. no primary
       * key). The setup itself still succeeds; callers may surface this as
       * a non-fatal warning.
       */
      dataWarning?: string;
    }
  | { success: false; error: string }
  /**
   * Returned by the progress-aware setup IPC when the user aborted partway
   * through. The handler stops on the next abort-checkpoint and rolls back
   * any partial on-disk state (best-effort `rm -rf` of the target folder if
   * we created it).
   */
  | { success: false; aborted: true; error: string }
  /**
   * Returned by setup/setupWithProgress IPC when the Personal-mode 200-object
   * limit is exceeded. The renderer must dispatch to the UpgradeDialog via
   * `proUpgradePromptStore.showPrompt` rather than displaying the literal
   * `'requires_pro'` string to the user.
   */
  | SetupBlueprintProRequiredResult;

/**
 * Coarse phase identifier for a Database Blueprint setup operation. Drives the
 * progress dialog's status text + which progress bar style to show.
 *  - `preparing`: ensure folder, sanity checks, pre-flight.
 *  - `fetching`: pulling schema metadata from the source DB (longest step
 *    for big DBs; not granular — emitter doesn't expose per-object events).
 *  - `writing`: writing the per-object .sql files (granular, has index/total).
 *  - `metadata`: writing sidecar, baseline cache, MetaInfo, manifest, README.
 *  - `aborting`: abort requested; we're waiting for the in-flight step to
 *    finish before unwinding.
 *  - `done`: setup completed successfully.
 *  - `aborted`: setup was cancelled by the user.
 *  - `error`: setup failed.
 */
export type BlueprintSetupPhase =
  | 'preparing'
  | 'fetching'
  | 'writing'
  | 'metadata'
  | 'aborting'
  | 'done'
  | 'aborted'
  | 'error';

/**
 * One progress event emitted by the progress-aware setup IPC over the
 * `database-blueprint-setup-progress` channel. Keyed by `operationId` so the
 * renderer can multiplex multiple in-flight ops (though v1 only ever runs
 * one at a time).
 */
export type BlueprintSetupProgressEvent = {
  operationId: string;
  phase: BlueprintSetupPhase;
  /** Human-readable log line, shown verbatim in the scrolling log. */
  message: string;
  /** When the phase is granular (currently `writing`), 1-based index. */
  index?: number;
  /** Total count for the granular phase (currently total files to write). */
  total?: number;
};

/**
 * Object counts reported in README + setup result.
 */
export type BlueprintObjectCounts = {
  tables: number;
  views: number;
  procedures: number;
  functions: number;
  triggers: number;
  userTypes: number;
  sequences: number;
  synonyms: number;
  permissions: number;
  schemas: number;
  packages: number;
};

/**
 * IPC request: read the blueprint's jamsql-blueprint.json from disk.
 */
export type ReadBlueprintMetadataRequest = {
  folderPath: string;
};

export type ReadBlueprintMetadataResult =
  | { success: true; metadata: BlueprintMetadata }
  | { success: false; error: string; notFound?: boolean };

/**
 * Preview-refresh — describes what a Refresh from DB would do without writing.
 *
 * `added`: new file (DB has an object the folder doesn't).
 * `modified`: file exists locally but the would-emit DDL differs.
 * `unchanged`: file exists locally and matches the would-emit DDL.
 * `removed`: file exists locally under a managed sub-folder but the DB
 *            has no matching object (would be deleted by a clean refresh).
 */
export type RefreshChangeStatus = 'added' | 'modified' | 'unchanged' | 'removed';

/**
 * `kind` distinguishes DDL (`.sql`) files from blueprint-data upsert sidecars
 * (`.data.sql`). Slice 4.4 added this so downstream consumers (the unified
 * Refresh-from-DB dialog, bundle builder, three-way merge) can group / filter
 * data-only changes without re-parsing the relPath suffix.
 */
export type RefreshChangeKind = 'schema' | 'data';

export type RefreshChange = {
  relPath: string;
  absPath: string;
  status: RefreshChangeStatus;
  kind: RefreshChangeKind;
  /** New content (only when added/modified). */
  newContent?: string;
  /** Existing content on disk (only when modified/removed). */
  oldContent?: string;
};

export type PreviewRefreshRequest = {
  folderPath: string;
  linkLabel: string;
};

export type PreviewRefreshResult =
  | {
      success: true;
      changes: RefreshChange[];
      counts: { added: number; modified: number; unchanged: number; removed: number };
    }
  | { success: false; error: string };

/**
 * Phase-2 unified Refresh-from-DB flow — a `PreviewBundle` is the staged,
 * cached outcome of a "preview refresh" against a single linked DB. It holds
 * everything the renderer needs to render the preview UI AND everything the
 * `writeBundle` step needs to apply the refresh, so the actual DDL/data fetch
 * happens once even if the user re-renders the dialog multiple times before
 * confirming.
 *
 * Bundles live in an in-process LRU (see `electron/database-blueprint/bundleCache.ts`)
 * with a short TTL — they are not persisted across app restarts.
 */
export type PreviewBundleId = string;

export type PreviewBundleLink = {
  connectionId: string;
  connectionDisplayName: string;
  database: string;
  engine: UserFacingEngineId;
  label?: string;
};

export type PreviewBundle = {
  bundleId: PreviewBundleId;
  folderPath: string;
  link: PreviewBundleLink;
  /** Epoch ms at which this bundle was built. Drives LRU expiry. */
  createdAt: number;
  /**
   * Files that drifted (local-vs-baseline) at the moment the bundle was built.
   * Surfaces in the preview dialog's "local changes will be overwritten" banner.
   *
   * `reason` mirrors `DriftedFile.reason` in `electron/database-blueprint/detectDrift.ts`.
   * Only entries with NO reason may be described to the user as local edits;
   * `'no_baseline'` means Jam has no sync record for this link and therefore
   * cannot tell whether the file was edited. Files a sibling link's refresh
   * wrote are not reported at all.
   */
  driftedFiles: Array<{ relPath: string; modifiedAt: number; reason?: 'no_baseline' }>;
  /**
   * `true` when this link has no baseline cache (`.jamsql/snapshots/<label>/`)
   * on disk yet, so a confirmed refresh has something to RECORD even when it
   * has no file to rewrite. Until it is recorded, cross-link ownership is
   * unknown and every link in the folder refreshes in reduced-safety mode
   * (`multiLinkSafety.ts`).
   */
  baselineMissing: boolean;
  /** DDL changes (`.sql`) the refresh would produce. */
  schemaChanges: RefreshChange[];
  /** Data-blueprint sidecar (`.data.sql`) changes the refresh would produce. */
  dataChanges: RefreshChange[];
  /**
   * Sidecar files (manifest, baseline cache snapshot, README, etc.) the
   * writeBundle step will write verbatim. Captured up-front so a confirmed
   * refresh cannot diverge from what the preview promised.
   */
  sidecarEntries: Array<{ relPath: string; content: string }>;
  /** Objects the build step refused to emit (no-PK, unsupported, etc.). */
  skipped: Array<{ schema: string; name: string; reason: string }>;
  /**
   * The emitter snapshot that produced `schemaChanges`. Carried so
   * `writeBundle` can persist `.jamsql/snapshot.json` without re-fetching
   * the schema — guarantees byte-for-byte parity between what the preview
   * promised and what lands on disk.
   *
   * Typed as `unknown` here so the renderer-shared types module doesn't
   * have to depend on `SchemaSnapshot`; the main-process `writeBundle`
   * narrows back to `SchemaSnapshot` at the boundary.
   */
  snapshot: unknown;
  /** Per-kind object counts; needed to regenerate the README on apply. */
  counts: BlueprintObjectCounts;
  /**
   * Engine the emitter actually ran as (mock-engine links resolve to
   * `'mssql'`). Persisted in the snapshot sidecar.
   */
  emitterEngine: UserFacingEngineId;
  /**
   * The link label this bundle was built for. Reused by `writeBundle` to
   * update `lastSyncAt` and to key the baseline cache.
   */
  linkLabel: string;
  /**
   * P2 — identity fingerprint captured at build time: a SHA-256 of the
   * manifest settings that affect bundle contents (exclusions, included
   * object types/schemas, blueprint-data table selection) PLUS the targeted
   * link identity (label + connectionId + database + engine). `writeBundle`
   * recomputes this under the manifest lock and rejects the apply if it
   * differs, so a bundle built before the user changed settings / switched
   * the active link can never overwrite the folder with obsolete output.
   * See `computeBundleFingerprint` in `electron/database-blueprint/bundleFingerprint.ts`.
   */
  fingerprint: string;
  /**
   * P1 — set when the blueprint is in the unsafe multi-link state at build time:
   * it is linked to >1 DB AND at least one OTHER link is missing its per-link
   * baseline cache on disk (e.g. a fresh clone). In this state cross-link
   * ownership is unknown, so the refresh:
   *   - does NOT surface other links' files as `removed` (the preview won't
   *     promise a deletion), and
   *   - `writeBundle` skips the destructive `removed` prune entirely.
   * The link's own files ARE still written and its baseline IS rebuilt, so
   * refreshing each link once heals the blueprint. The renderer shows a one-line
   * reduced-safety warning naming the missing links. Absent on single-link
   * blueprints and on healthy multi-link blueprints (no behaviour change).
   */
  unsafeMultiLink?: { missingLabels: string[] };
};

/**
 * Progress event emitted while `writeBundle` applies a `PreviewBundle`.
 *  - `preparing`: pre-write validation (folder still exists, etc.).
 *  - `writing`: writing per-file entries (granular, has currentIndex/totalCount).
 *  - `pruning`: deleting on-disk files no longer in the bundle.
 *  - `done`: write completed.
 */
export type BundleWriteProgress = {
  bundleId: PreviewBundleId;
  phase: 'preparing' | 'writing' | 'pruning' | 'done';
  /** 1-based index into the total file count. */
  currentIndex: number;
  totalCount: number;
  currentRelPath?: string;
};

/**
 * Phase identifier for a Database Blueprint preview-refresh operation. Narrower
 * than `BlueprintSetupPhase` because preview never writes files / metadata —
 * it just fetches the DB snapshot and diffs against the folder.
 *
 *  - `preparing`: read manifest, validate label, gather link config.
 *  - `fetching`: pulling schema metadata from the source DB (granular —
 *    emits per-object events with index/total).
 *  - `diffing`: comparing emitted DDL against on-disk files.
 *  - `error`: preview failed.
 */
export type BlueprintPreviewPhase =
  | 'preparing'
  | 'fetching'
  | 'diffing'
  | 'error';

/**
 * Legacy preview-refresh progress event shape (retired in Slice 4.6). Kept
 * here purely as a stable type alias for any downstream code that still
 * imports it. The corresponding IPC channel has been removed; the new
 * Refresh-from-DB flow uses `BundleWriteProgress` instead.
 */
export type BlueprintPreviewProgressEvent = {
  operationId: string;
  phase: BlueprintPreviewPhase;
  /** Human-readable line, shown verbatim in the progress view. */
  message: string;
  /** When the phase is granular (currently `fetching`), 1-based index. */
  index?: number;
  /** Total count for the granular phase (currently total objects to fetch). */
  total?: number;
};

/**
 * Phase identifier for a Database Blueprint refresh-from-DB operation. Narrower
 * than `BlueprintSetupPhase` because refresh never creates the folder /
 * jamsql-blueprint.json from scratch — it just re-emits DDL for an existing
 * blueprint link.
 *
 *  - `preparing`: read manifest, validate label, gather link config.
 *  - `fetching`: pulling schema metadata from the source DB (granular —
 *    emits per-object events with index/total).
 *  - `writing`: writing the per-object .sql files (granular, has index/total).
 *  - `metadata`: writing sidecar, baseline cache, MetaInfo, manifest, README.
 *  - `done`: refresh completed successfully.
 *  - `error`: refresh failed.
 */
export type BlueprintRefreshPhase =
  | 'preparing'
  | 'fetching'
  | 'writing'
  | 'metadata'
  | 'done'
  | 'error';

/**
 * Legacy refresh progress event shape (retired in Slice 4.6). Kept here
 * purely as a stable type alias for any downstream code that still
 * imports it. The corresponding IPC channel has been removed; the new
 * Refresh-from-DB flow uses `BundleWriteProgress` instead.
 */
export type BlueprintRefreshProgressEvent = {
  operationId: string;
  phase: BlueprintRefreshPhase;
  /** Human-readable line, shown verbatim in the progress overlay. */
  message: string;
  /** When the phase is granular (currently `fetching` / `writing`), 1-based index. */
  index?: number;
  /** Total count for the granular phase. */
  total?: number;
};

/**
 * Three-way merge — compares baseline (last refresh), local on-disk, and
 * what the DB would emit now. See `electron/database-blueprint/threeWayMerge.ts`
 * for the full status semantics.
 */
export type ThreeWayStatus =
  | 'clean'
  | 'local-only'
  | 'db-only'
  | 'both-clean-merge'
  | 'conflict'
  | 'added-local'
  | 'added-db'
  | 'added-both-clean'
  | 'added-both-conflict'
  | 'removed-local'
  | 'removed-db'
  | 'removed-both';

export type ThreeWayEntry = {
  relPath: string;
  status: ThreeWayStatus;
  baselineContent: string | null;
  localContent: string | null;
  dbContent: string | null;
  /**
   * Finding #13 — when `status === 'removed-local'`, true if the DB also
   * moved off-baseline (asymmetric removal-vs-modify). Always absent or
   * false for other statuses.
   */
  dbChanged?: boolean;
  /**
   * Finding #13 — when `status === 'removed-db'`, true if local also moved
   * off-baseline (asymmetric removal-vs-modify). Always absent or false
   * for other statuses.
   */
  localChanged?: boolean;
};

export type ThreeWayMergeRequest = {
  folderPath: string;
  linkLabel: string;
  /**
   * Correlates live progress events emitted over the
   * `database-blueprint-three-way-merge-progress` channel with this specific
   * in-flight merge so the dialog can ignore stale events from a previous run.
   * Optional — older callers (and the MCP/headless paths) don't subscribe.
   */
  requestId?: string;
};

/**
 * One live-progress event emitted while a three-way merge computes, over the
 * `database-blueprint-three-way-merge-progress` channel. `message` is a terse,
 * low-cardinality human-readable line (e.g. "Reading Tables/dbo.Orders.sql…").
 */
export type ThreeWayMergeProgressEvent = {
  requestId: string;
  message: string;
};

export type ThreeWayMergeResult =
  | {
      success: true;
      entries: ThreeWayEntry[];
      counts: Record<ThreeWayStatus, number>;
    }
  | { success: false; error: string };

/**
 * Renderer-side representation of a `.data.json` sidecar file.
 * Blueprints the on-disk `DataCacheSidecar` shape from `electron/database-blueprint/blueprintDataSidecar.ts`
 * without importing the electron-side module into the renderer bundle.
 */
export type DatabaseBlueprintSidecar = {
  version?: number;
  capturedAt?: string;
  pk: ReadonlyArray<string>;
  columns: ReadonlyArray<{
    name: string;
    type: string;
    isIdentity?: boolean;
    pgArrayElementType?: string;
    isWritable?: boolean;
  }>;
  rows: ReadonlyArray<ReadonlyArray<unknown>>;
};

/**
 * Response type for the `database-blueprint-read-sidecar` IPC channel.
 * Returns `{ success: true, sidecar: null }` when the file is absent
 * so the blueprint-data viewer can render an empty state.
 */
export type ReadSidecarResult =
  | { success: true; sidecar: DatabaseBlueprintSidecar | null }
  | { success: false; error: string };

/**
 * IPC request: write an EDITED table rowset back to the blueprint folder
 * (Table Explorer Blueprint-edit mode Save). Regenerates the `.data.sql`
 * upsert + `.data.json` sidecar from the supplied rows + column metadata —
 * NEVER queries a database. The renderer merges pending changes into the full
 * rowset before calling this.
 */
export type ApplyBlueprintTableEditsRequest = {
  folderPath: string;
  schema: string;
  name: string;
  engine: UserFacingEngineId;
  pk: string[];
  /** Full column set in positional order matching each row in `rows`. */
  columns: Array<{
    name: string;
    type: string;
    isIdentity?: boolean;
    pgArrayElementType?: string;
    isWritable?: boolean;
  }>;
  /** Edited rows as positional arrays aligned to `columns`. */
  rows: Array<Array<unknown>>;
};

export type ApplyBlueprintTableEditsResult =
  | { success: true; dataSqlPath: string; sidecarPath: string; rows: number }
  | { success: false; error: string };

/**
 * Git status for the blueprint folder (subset of `simple-git`'s StatusResult).
 */
export type BlueprintGitStatus = {
  current: string | null;
  tracking: string | null;
  ahead: number;
  behind: number;
  staged: string[];
  not_added: string[];
  modified: string[];
  deleted: string[];
  conflicted: string[];
  files: Array<{ path: string; index: string; working_dir: string }>;
  isClean: boolean;
};

/**
 * WS-D step 5 — offline data-cache rebuild (recovers `Data/*.data.json`
 * sidecars from their sibling `.data.sql` upsert scripts alone, no live DB).
 * Backed by `electron/database-blueprint/sidecar-rebuild/dataCacheRebuilder.ts`.
 */
export type RebuildBlueprintDataCacheRequest = {
  folderPath: string;
  engine: UserFacingEngineId;
};

export type RebuildBlueprintDataCacheResult =
  | {
      success: true;
      /** `Data/…` relative paths of the sidecars written. */
      rebuilt: string[];
      /** Files the rebuilder couldn't reconstruct, with a human-readable reason. */
      skipped: Array<{ file: string; reason: string }>;
      /** Known representational caveats (dates-as-strings, booleans-as-0/1,
       *  computed columns omitted, etc.) — never silent, always surfaced. */
      fidelityNotes: string[];
    }
  | { success: false; error: string };
